import { useState, useEffect } from "react";
import { Alert, Box, Button, Toolbar, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import PasswordInput from "components/common/PasswordInput";
import { validatePassword } from "common/utils";
import AuthService from "services/auth";
import TextField from "components/common/TextField";
import Checkbox from "components/common/Checkbox";
import {
  TOS_VERSION,
  hasAcceptedTermsOfService,
} from "components/context/CheckTerms";
import { showNewPasswordModal } from "components/context/NewPasswordPrompt";
import { celebrate } from "common/celebrations";
import { refreshToken } from "services/api";
import { FayeAppBar } from "components/layout/NavigationLayout";
import { AccountType } from "protogen/common_pb";

export default () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const currentUser = AuthService.getCurrentUser();
  const [password, setPassword] = useState("");
  const [hasAcceptedToS, setHasAcceptedToS] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState(false);

  const needsTos = !hasAcceptedTermsOfService();
  const needsPassword = !!showNewPasswordModal();

  useEffect(() => {
    if (!needsTos && !needsPassword) {
      navigate("/home", { replace: true });
    }
  }, []);

  const { request: createPasswordRequest, loading: passwordLoading } =
    AuthService.useCreatePassword();
  const { request: acceptTosRequest, loading: tosLoading } =
    AuthService.useAcceptTermsOfService();

  const loading = passwordLoading || tosLoading;
  const isComplete =
    (!needsPassword || !!password.trim()) && (!needsTos || hasAcceptedToS);

  const handleContinue = async () => {
    const validationErrors: Record<string, string> = {};

    if (needsPassword) {
      Object.assign(validationErrors, validatePassword(password));
    }

    if (needsTos && !hasAcceptedToS) {
      validationErrors.termsOfService = "You must accept the terms of service";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (needsTos) {
        await acceptTosRequest({
          accountType: AccountType.MEMBER,
          termsOfServiceVersion: TOS_VERSION,
        });
        await refreshToken();
      }

      if (needsPassword) {
        await refreshToken();
        if (!AuthService.getCurrentUser()?.isPasswordSet) {
          await createPasswordRequest({ password });
        }
        localStorage.removeItem("isUrlLogin");
      }

      setCompleted(true);
    } catch {
      setErrors({ general: "Something went wrong. Please try again." });
    }
  };

  useEffect(() => {
    if (completed) {
      const confettiTimeout = window.setTimeout(() => celebrate(), 200);
      const redirectTimeout = window.setTimeout(() => {
        navigate("/home?from=signup", { replace: true });
      }, 2500);
      return () => {
        window.clearTimeout(confettiTimeout);
        window.clearTimeout(redirectTimeout);
      };
    }
  }, [completed, navigate]);

  if (completed) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#FDFAF7",
          gap: "24px",
          padding: "24px",
        }}
      >
        <img
          src="/assets/images/nux/high-five.gif"
          style={{
            maxWidth: isMobile ? "280px" : "400px",
            borderRadius: "16px",
          }}
        />
        <Typography variant={isMobile ? "h2" : "h1"}>
          You're all set!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#FDFAF7",
      }}
    >
      <FayeAppBar />
      <Toolbar sx={{ minHeight: "50px" }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          padding: isMobile ? "16px" : "48px 32px",
          gap: isMobile ? "20px" : "64px",
          alignItems: "center",
          flex: 1,
        }}
      >
        {!isMobile && (
          <Box sx={{ flex: 1, maxWidth: "max-content", display: "flex" }}>
            <img
              src="/assets/images/nux/signup-confirmation.png"
              style={{
                maxHeight: "585px",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "max-content",
              }}
            />
          </Box>
        )}

        {isMobile && (
          <img
            src="/assets/images/nux/signup-confirmation-mobile.png"
            style={{
              width: "100%",
              borderRadius: "0",
              maxHeight: "30vh",
              objectFit: "cover",
              borderBottom: "1px solid #D4D4D4",
            }}
          />
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            maxWidth: "570px",
            width: "100%",
            padding: isMobile ? "0 8px" : "",
            gap: "16px",
          }}
        >
          <Typography variant={isMobile ? "h2" : "h1"}>
            👋 Welcome to Faye
          </Typography>

          <Typography variant="body" sx={{ marginBottom: "8px" }}>
            You're almost there! Set a password to keep your account secure and
            you'll be ready to go.
          </Typography>

          {errors.general && <Alert severity="error">{errors.general}</Alert>}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleContinue();
            }}
          >
            {needsPassword && (
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                name="email"
                autoComplete="username"
                value={currentUser?.email || ""}
              />
            )}

            {needsPassword && (
              <PasswordInput
                password={password}
                onChange={(pw: string) => {
                  setPassword(pw);
                  if (errors.password) {
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }
                }}
                autoComplete="new-password"
                sx={{ width: "100%", marginTop: "16px" }}
              />
            )}

            {needsTos && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  marginTop: "16px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    checked={hasAcceptedToS}
                    onChange={() => {
                      setHasAcceptedToS(!hasAcceptedToS);
                      if (errors.termsOfService) {
                        setErrors((prev) => ({
                          ...prev,
                          termsOfService: "",
                        }));
                      }
                    }}
                  />
                  <Typography variant="body">
                    I agree to the{" "}
                    <a
                      href="https://www.findfaye.com/client-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>
                    .
                  </Typography>
                </label>
                {errors.termsOfService && (
                  <Typography variant="caption" color="error">
                    {errors.termsOfService}
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                disabled={loading || !isComplete}
              >
                Get Started
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Box>
  );
};
